/**
 * The subscription dates are calendar days, but OPDON persists them as full UTC
 * instants (the create flow anchors the picked day at 12:00:00Z) and returns them
 * that way (e.g. "2026-10-06T12:00:00Z"). The FE treats them as ISO `yyyy-MM-dd`
 * throughout — that's what the `useDateIsoBinding` picker bridge strictly parses —
 * so the read mapper strips the time part. Feeding the raw instant to the binding
 * renders an empty picker (strict `YYYY-MM-DD` parse fails), and feeding it to a
 * format-less DatePickerInput makes flowbite-datepicker misparse it as garbage
 * like "10/06/0178" (same failure the donation mapper guards against).
 */
function toCalendarDate(value) {
    if (!value)
        return "";
    return value.split("T")[0];
}
/**
 * Re-anchors a calendar day (`yyyy-MM-dd`) to the UTC instant OPDON expects on the
 * update wire. Mirrors the create flow's `${date}T12:00:00Z`: 12:00 UTC keeps the
 * calendar day stable across any reasonable viewer timezone, and the explicit `Z`
 * gives Npgsql the UTC Kind its `timestamp with time zone` columns require — a bare
 * `yyyy-MM-dd` deserializes as Kind=Unspecified and makes the save fail with a 500.
 * Empty input → `undefined` so the field is dropped (backend leaves it unchanged)
 * rather than sent as an unparseable "".
 */
function toSubscriptionDateInstant(value) {
    const date = toCalendarDate(value);
    return date ? `${date}T12:00:00Z` : undefined;
}
/**
 * Maps a BFF subscription response to the domain Subscription entity.
 */
export function mapApiSubscriptionToSubscription(apiSubscription) {
    return {
        id: String(apiSubscription.subscriptionId),
        donorId: apiSubscription.donorId ? String(apiSubscription.donorId) : "",
        // CRM references behind the donor — these (not donorId) are what the donor link
        // navigates to. Resolved by OPDON when the donor is loaded.
        contactId: apiSubscription.contactId ? String(apiSubscription.contactId) : undefined,
        // Referent contact name (Organization subscription) — same role as on a donation.
        contactDisplayName: apiSubscription.contactDisplayName ?? undefined,
        organizationId: apiSubscription.organizationId
            ? String(apiSubscription.organizationId)
            : undefined,
        donorType: apiSubscription.donorType ?? undefined,
        // OPDON resolves this live from the OPUS snapshot on each read.
        donorName: apiSubscription.donorDisplayName ?? "",
        amount: apiSubscription.amount ?? 0,
        initialAmount: apiSubscription.initialAmount ?? undefined,
        installmentCount: apiSubscription.installmentCount ?? undefined,
        currency: apiSubscription.currency,
        // FrequencyPublic: Monthly | Quarterly | Biannual | Annual
        frequency: apiSubscription.frequency,
        status: apiSubscription.status,
        paymentMethod: apiSubscription.paymentMethod,
        startDate: toCalendarDate(apiSubscription.startDate),
        endDate: toCalendarDate(apiSubscription.endDate) || undefined,
        // The read model calls this nextDueDate; the DTO and the write model use nextPaymentDate.
        nextDueDate: toCalendarDate(apiSubscription.nextPaymentDate) || undefined,
        campaignId: apiSubscription.campaignId ? String(apiSubscription.campaignId) : undefined,
        campaignName: apiSubscription.campaignNameSnapshot ?? undefined,
        notes: apiSubscription.note ?? undefined,
        createdAt: apiSubscription.createdAt ?? "",
        updatedAt: apiSubscription.updatedAt ?? "",
    };
}
/**
 * Maps the editable subscription state to the BFF/OPDON update wire contract. Only the fields
 * the detail page can edit are forwarded. Note the read-model's `nextDueDate` maps to the
 * write-model's `nextPaymentDate`.
 *
 * The calendar-day dates are re-anchored to `T12:00:00Z` UTC instants via
 * {@link toSubscriptionDateInstant} (mirrors the create flow); an empty start date is
 * dropped so the backend leaves it unchanged, while a cleared end / next-payment date
 * is sent as `null` — the explicit clear signal (indefinite / recompute from schedule).
 */
export function toUpdateSubscriptionPayload(subscription) {
    return {
        status: subscription.status,
        amount: subscription.amount,
        initialAmount: subscription.initialAmount ?? null,
        installmentCount: subscription.installmentCount ?? null,
        frequency: subscription.frequency,
        paymentMethod: subscription.paymentMethod,
        startDate: toSubscriptionDateInstant(subscription.startDate),
        endDate: toSubscriptionDateInstant(subscription.endDate) ?? null,
        nextPaymentDate: toSubscriptionDateInstant(subscription.nextDueDate) ?? null,
        campaignId: subscription.campaignId,
    };
}
